var _excluded = ["selectedOption", "placeholder", "viewModePlaceholder", "size", "isDisabled", "isReadOnly", "isViewMode", "isInvalid", "isLoading", "isOpen", "setIsOpen", "shouldHideClearButton", "clearButtonAriaLabel", "data-testid", "onClear", "onClick", "onFocus", "onBlur", "valueRenderer"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }

import { Input } from '@chakra-ui/react';
import { useMergeRefs } from '@floating-ui/react';
import { isEmpty } from 'lodash';
import { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import { useBaseMenuContext } from '../../../../containers/BaseMenu/hooks';
import { themeSpaces } from '../../../../theme/foundations';
import { useTestId } from '../../../../utils';
import { useMultiStyleConfig } from '../../../../utils/use-style-config';
import { InputGroup, useFormControlContext } from '../../../internal';
import { A11yStatusMessage } from './A11yStatusMessage';
import { ClearButton } from './ClearButton';
import { TriggerRightElement } from './TriggerRightElement';
import { TriggerValue } from './TriggerValue';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";

var TriggerComponent = (_ref, ref) => {
  var _selectedOption$label;

  var {
    selectedOption,
    placeholder = 'Select',
    viewModePlaceholder,
    size = 'large',
    isDisabled,
    isReadOnly,
    isViewMode,
    isInvalid,
    isLoading,
    isOpen,
    setIsOpen,
    shouldHideClearButton = false,
    clearButtonAriaLabel = 'Clear selected option',
    'data-testid': dataTestId = 'select',
    onClear,
    onClick,
    onFocus: _onFocus,
    onBlur: _onBlur,
    valueRenderer
  } = _ref,
      otherProps = _objectWithoutProperties(_ref, _excluded);

  var getTestId = useTestId(dataTestId);
  var isFormContext = !isEmpty(useFormControlContext());
  var formFieldPlaceholder = viewModePlaceholder !== null && viewModePlaceholder !== void 0 ? viewModePlaceholder : placeholder;
  var standalonePlaceholder = isViewMode && viewModePlaceholder ? viewModePlaceholder : placeholder;
  var placeholderValue = isFormContext ? formFieldPlaceholder : standalonePlaceholder;
  var disableClick = isDisabled || isLoading || isReadOnly || isViewMode;
  var showClearButton = !shouldHideClearButton && Boolean(selectedOption) && !disableClick;
  var showRightElement = !isViewMode || isLoading;
  var styles = useMultiStyleConfig('SelectNew', {
    hasCustomValue: Boolean(valueRenderer)
  });
  var [rightElementWidth, setRightElementWidth] = useState(0);
  var [clearButtonWidth, setClearButtonWidth] = useState(0);
  var inputRef = useRef(null);
  var {
    setActiveIndex
  } = useBaseMenuContext();
  var toggleMenu = useCallback(event => {
    if (disableClick) return;
    onClick === null || onClick === void 0 ? void 0 : onClick(event);

    if (!isOpen) {
      var _inputRef$current;

      (_inputRef$current = inputRef.current) === null || _inputRef$current === void 0 ? void 0 : _inputRef$current.focus(); // TODO: Check if this is necessary after https://meliorisk.atlassian.net/browse/ME-54874

      setActiveIndex(0);
    }
  }, [disableClick, isOpen, onClick, setActiveIndex]);
  var [isFocused, setIsFocused] = useState(false); // This is to blur the input when closing the menu by clicking outside.

  useEffect(() => {
    var _inputRef$current2;

    if (isOpen || !isFocused) return;
    (_inputRef$current2 = inputRef.current) === null || _inputRef$current2 === void 0 ? void 0 : _inputRef$current2.focus();
  }, [isOpen, isFocused]);
  return (
    /*#__PURE__*/
    // Using chakra's `Input` so we can inherit the styles for the trigger from `form.utils.ts`.
    _jsxs(InputGroup, {
      "data-component": "SelectNew",
      children: [/*#__PURE__*/_jsx(Input, _objectSpread(_objectSpread(_objectSpread({
        ref: useMergeRefs([ref, inputRef]),
        "data-component": "SelectNew.Trigger",
        sx: styles['trigger']
      }, otherProps), getTestId('trigger')), {}, {
        "data-readonly": isReadOnly || isLoading || isViewMode || undefined,
        "data-loading": isLoading || undefined,
        "data-view-mode": isViewMode || undefined,
        "aria-disabled": isDisabled || undefined,
        tabIndex: disableClick ? -1 : 0 // We pass `placeholder` so the screen reader can announce the placeholder when the input is focused in form context.
        ,
        placeholder: placeholderValue // We pass `value` so the screen reader can announce the selected option.
        ,
        value: (_selectedOption$label = selectedOption === null || selectedOption === void 0 ? void 0 : selectedOption.label) !== null && _selectedOption$label !== void 0 ? _selectedOption$label : '' // We pass an empty function to suppress the warning about the input being read-only.
        ,
        onChange: () => null // We use `onMouseDown` instead of `onClick` to prevent the menu from opening when the user clicks the label in form context.
        ,
        onMouseDown: toggleMenu,
        onFocus: event => {
          if (isFocused) return;
          setIsFocused(true);
          _onFocus === null || _onFocus === void 0 ? void 0 : _onFocus(event);
        },
        onBlur: event => {
          if (isOpen || !isFocused) return;
          setIsFocused(false);
          _onBlur === null || _onBlur === void 0 ? void 0 : _onBlur(event);
        }
      })), /*#__PURE__*/_jsx(TriggerValue, _objectSpread({
        selectedOption: selectedOption,
        placeholder: placeholderValue,
        valueRenderer: valueRenderer,
        size: size,
        isInvalid: isInvalid,
        isFocused: isFocused,
        isDisabled: isDisabled,
        isLoading: isLoading,
        isReadOnly: isReadOnly,
        isViewMode: isViewMode,
        paddingRight: showClearButton ? "calc(".concat(clearButtonWidth, "px + ").concat(themeSpaces.xxl, ")") : "".concat(rightElementWidth, "px")
      }, getTestId('trigger-value'))), showRightElement && /*#__PURE__*/_jsx(TriggerRightElement, {
        isOpen: isOpen,
        isDisabled: isDisabled,
        isLoading: isLoading,
        isReadOnly: isReadOnly,
        isViewMode: isViewMode,
        setRightElementWidth: setRightElementWidth
      }), showClearButton && /*#__PURE__*/_jsx(ClearButton, _objectSpread(_objectSpread({
        "aria-label": clearButtonAriaLabel
      }, getTestId('clear-button')), {}, {
        onClick: event => {
          var _inputRef$current3;

          event.stopPropagation();
          onClear === null || onClear === void 0 ? void 0 : onClear();
          (_inputRef$current3 = inputRef.current) === null || _inputRef$current3 === void 0 ? void 0 : _inputRef$current3.focus();
        },
        setClearButtonWidth: setClearButtonWidth
      })), /*#__PURE__*/_jsx(A11yStatusMessage, {
        selectedOption: selectedOption
      })]
    })
  );
};

export var Trigger = /*#__PURE__*/forwardRef(TriggerComponent);
Trigger.displayName = 'SelectNew.Trigger';
//# sourceMappingURL=Trigger.js.map